import { t } from "i18next";
import {
    getShopVatList,
    resolveTvaFromList,
    Tranche,
    ZoneRecord,
} from "../shared";

export const INITIAL_ZONE_AMOUNT_FORM_DATA = {
    tva: "",
    remoteDebut: 0,
    endDistance: 0,
    countryData: "",
};

export const INITIAL_VALIDATION = {
    selectCountry: false,
    inputTva: false,
    isOverlap: false,
    isDistanceExist: false,
    isDistanceEdited: false,
    isTranchesEmpty: false,
};

/**
 * Formats zone amount data for storage (nested structure).
 * Each zone is one record with a tranches array.
 */
export function formatZoneAmount(
    dataLivraison: (ZoneRecord & { subrows?: Tranche[] })[]
) {
    if (!dataLivraison || !Array.isArray(dataLivraison)) {
        return [];
    }

    return dataLivraison.map((element) => {
        const tranches: Tranche[] = element.tiers || element.subrows || [];

        return {
            DebutDistance: element.DebutDistance,
            FinDistance: element.FinDistance,
            idTva: Number(element.idTva),
            TauxTva: Number(element.TauxTva),
            tiers: tranches,
        };
    });
}

/**
 * Normalizes raw data from DynamicTable/API into clean nested records.
 */
export function formatZoneAmountLivraisonData(
    zoneData: (ZoneRecord & { subrows?: Tranche[] })[]
) {
    return (zoneData || []).map((el) => ({
        DebutDistance: el.DebutDistance,
        FinDistance: el.FinDistance,
        idTva: el.idTva,
        TauxTva: el.TauxTva,
        tiers: el.subrows || el.tiers || [],
    }));
}

/**
 * Creates the payload for the API (add or update).
 * Each zone is one nested record with a tranches array.
 */
export function createDataZoneAmount(
    localStZoneLivraison: ZoneRecord[],
    zoneFormData: {
        tva: string;
        remoteDebut: number;
        endDistance: number;
        countryData: string;
    },
    tranches: Tranche[],
    selectedRowShops: Record<string, unknown>[],
    selectedRow: Record<string, unknown>,
    franchiseID: string,
    isUpdate: boolean
) {
    const tva = resolveTvaFromList(
        zoneFormData.tva,
        getShopVatList(selectedRowShops)
    );
    // Auto-append unlimited last tier if missing
    let finalTranches = [...tranches];
    if (
        finalTranches.length > 0 &&
        finalTranches[finalTranches.length - 1].MaxCommande !== 0
    ) {
        const lastTier = finalTranches[finalTranches.length - 1];
        finalTranches.push({
            MinCommande: lastTier.MaxCommande,
            MaxCommande: 0,
            FraisLivraison: 0,
        });
    }
    const newRecord = {
        DebutDistance: zoneFormData.remoteDebut,
        FinDistance: zoneFormData.endDistance,
        idTva: tva ? Number(tva.idTva) : 0,
        TauxTva: tva ? Number(tva.tauxTva) : Number(zoneFormData.tva),
        tiers: finalTranches,
    };

    if (isUpdate) {
        const oldDebut = selectedRow[t("remote Debut")];
        const oldFin = selectedRow[t("end Distance")];
        localStZoneLivraison = localStZoneLivraison.map((el) =>
            el.DebutDistance === oldDebut && el.FinDistance === oldFin
                ? newRecord
                : el
        );
    } else {
        localStZoneLivraison.push(newRecord);
    }

    localStZoneLivraison = formatZoneAmountLivraisonData(localStZoneLivraison);
    localStZoneLivraison = formatZoneAmount(localStZoneLivraison);
    localStZoneLivraison.sort((a, b) => a.DebutDistance - b.DebutDistance);

    return {
        shopId: selectedRowShops[0].shopId,
        franchiseID: franchiseID,
        key: "stZoneLivraisonMontant",
        data: localStZoneLivraison,
    };
}

/**
 * Extracts tranches from a group of zone records (for populating the form in edit mode).
 */
export function extractTranchesFromGroup(
    allRecords: (ZoneRecord & { subrows?: Tranche[] })[],
    debutDistance: number,
    finDistance: number
): Tranche[] {
    const zone = allRecords.find(
        (r) =>
            r.DebutDistance === debutDistance && r.FinDistance === finDistance
    );
    return zone?.tiers || zone?.subrows || [];
}

/**
 * Groups records by zone (DebutDistance+FinDistance) for table display.
 */
export function groupByZone(records: ZoneRecord[]) {
    const groups: Map<
        string,
        { debutDistance: number; finDistance: number; records: ZoneRecord[] }
    > = new Map();

    (records || []).forEach((r) => {
        const key = `${r.DebutDistance}-${r.FinDistance}`;
        if (!groups.has(key)) {
            groups.set(key, {
                debutDistance: r.DebutDistance,
                finDistance: r.FinDistance,
                records: [],
            });
        }
        groups.get(key)!.records.push(r);
    });

    return Array.from(groups.values());
}

export function checkOverlap(
    listDistanceZone: { debut: number; end: number }[],
    selectedRows: Record<string, unknown>[],
    validation: {
        selectCountry: boolean;
        inputTva: boolean;
        isOverlap: boolean;
        isDistanceExist: boolean;
        isDistanceEdited: boolean;
        isTranchesEmpty: boolean;
    },
    zoneFormData: {
        tva: string;
        remoteDebut: number;
        endDistance: number;
        countryData: string;
    },
    isDuplicate: boolean = false
) {
    const isEdit = selectedRows.length > 0 && !isDuplicate;
    const isAdd = selectedRows.length === 0 || isDuplicate;

    const shouldCheck =
        (isAdd && validation.isDistanceEdited) ||
        (isEdit &&
            !(
                zoneFormData.remoteDebut ===
                    selectedRows[0][t("remote Debut")] &&
                zoneFormData.endDistance === selectedRows[0][t("end Distance")]
            ) &&
            validation.isDistanceEdited);
    if (!shouldCheck) return false;

    const zonesToCheck = isEdit
        ? listDistanceZone.filter(
              (el) =>
                  !(
                      Number(el.debut) ===
                          Number(selectedRows[0][t("remote Debut")]) &&
                      Number(el.end) ===
                          Number(selectedRows[0][t("end Distance")])
                  )
          )
        : listDistanceZone;

    return zonesToCheck.some(
        (el) =>
            (zoneFormData.remoteDebut > el.debut &&
                zoneFormData.remoteDebut < el.end) ||
            (zoneFormData.remoteDebut === el.debut &&
                zoneFormData.endDistance !== 0) ||
            (zoneFormData.remoteDebut < el.debut &&
                zoneFormData.endDistance > el.end) ||
            (zoneFormData.endDistance > el.debut &&
                zoneFormData.endDistance < el.end) ||
            (zoneFormData.remoteDebut < el.end &&
                zoneFormData.endDistance === el.end)
    );
}
